import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Mail, MapPin, Phone } from "lucide-react";
import { useContent } from "@/content/ContentContext";
import { useLang } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.22, 1, 0.36, 1];

const inputCls =
  "w-full border-b border-line bg-transparent py-3 text-base font-medium text-cream placeholder:text-sage/50 outline-none transition-colors duration-300 focus:border-harvest";

export default function EnquiryForm() {
  const content = useContent();
  const { t } = useLang();
  const [searchParams] = useSearchParams();
  const c = content.contact;
  const contactRows = [
    { icon: Phone, label: t("callWhatsapp"), value: c.phone, href: `tel:${c.phone.replace(/\s/g, "")}`, testid: "contact-phone-link" },
    { icon: Mail, label: t("emailLabel"), value: c.email, href: `mailto:${c.email}`, testid: "contact-email-link" },
    { icon: MapPin, label: t("works"), value: c.address, href: null, testid: "contact-address" },
  ];
  const DEALER_OPTION = "Dealership — Dealer Banana Hai";
  const productOptions = [...content.products.map((p) => p.name), "Complete Project", DEALER_OPTION];
  const initialProduct =
    searchParams.get("interest") === "dealership" ? DEALER_OPTION : productOptions[0];
  const [form, setForm] = useState({
    name: "",
    phone: "",
    product: initialProduct,
    requirement: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success(t("toastSuccess"));
      setForm({ name: "", phone: "", product: productOptions[0], requirement: "" });
    } catch {
      toast.error(t("toastError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="enquiry-section"
      className="border-t border-line bg-moss/40"
    >
      <div className="mx-auto max-w-[1440px] px-6 py-28 lg:px-12 lg:py-40">
        <div className="grid gap-16 lg:grid-cols-12 lg:gap-20">
          <div className="lg:col-span-5">
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease }}
              className="text-xs uppercase tracking-[0.3em] text-harvest"
            >
              {t("contactOverline")}
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: 0.1, ease }}
              data-testid="enquiry-heading"
              className="mt-6 font-display text-4xl font-medium leading-[1.05] tracking-tight text-cream sm:text-5xl"
            >
              {t("contactHeadA")} <br />
              {t("contactHeadMid")} <span className="italic text-harvest">{t("contactHeadAccent")}</span> {t("contactHeadPost")}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: 0.15, ease }}
              className="mt-6 max-w-md text-sm leading-relaxed text-sage sm:text-base"
            >
              {t("contactPara")}
            </motion.p>

            <div className="mt-12 space-y-8">
              {contactRows.map((row, i) => (
                <motion.div
                  key={row.label}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.7, delay: i * 0.08, ease }}
                  className="flex items-start gap-4"
                >
                  <span className="border border-line p-3 text-harvest">
                    <row.icon className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-sage">
                      {row.label}
                    </p>
                    {row.href ? (
                      <a
                        href={row.href}
                        data-testid={row.testid}
                        className="mt-1 block text-base text-cream transition-colors duration-300 hover:text-harvest"
                      >
                        {row.value}
                      </a>
                    ) : (
                      <p data-testid={row.testid} className="mt-1 text-base text-cream">
                        {row.value}
                      </p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <motion.form
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 48 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.1, ease }}
            data-testid="enquiry-form"
            className="border border-line bg-moss p-8 sm:p-12 lg:col-span-7"
          >
            <p className="font-display text-2xl italic text-cream">
              {t("enquiryForm")}
            </p>
            <p className="mt-2 text-xs uppercase tracking-[0.2em] text-sage">
              {t("reply24")}
            </p>

            <div className="mt-10 grid gap-8 sm:grid-cols-2">
              <div>
                <label htmlFor="enquiry-name" className="text-xs uppercase tracking-[0.2em] text-sage">
                  {t("labelName")}
                </label>
                <input
                  id="enquiry-name"
                  data-testid="enquiry-name-input"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Ramesh Patel"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="enquiry-phone" className="text-xs uppercase tracking-[0.2em] text-sage">
                  {t("labelPhone")}
                </label>
                <input
                  id="enquiry-phone"
                  data-testid="enquiry-phone-input"
                  required
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="+91 98290 57721"
                  className={inputCls}
                />
              </div>
            </div>

            <div className="mt-8">
              <label htmlFor="enquiry-product" className="text-xs uppercase tracking-[0.2em] text-sage">
                {t("labelProduct")}
              </label>
              <select
                id="enquiry-product"
                data-testid="enquiry-product-select"
                value={form.product}
                onChange={set("product")}
                className={`${inputCls} cursor-pointer appearance-none bg-moss`}
              >
                {productOptions.map((p) => (
                  <option key={p} value={p} className="bg-moss text-cream">
                    {p}
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-8">
              <label htmlFor="enquiry-requirement" className="text-xs uppercase tracking-[0.2em] text-sage">
                {t("labelRequirement")}
              </label>
              <textarea
                id="enquiry-requirement"
                data-testid="enquiry-requirement-input"
                required
                rows={4}
                value={form.requirement}
                onChange={set("requirement")}
                placeholder={t("reqPlaceholder")}
                className={`${inputCls} resize-none`}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="enquiry-submit-button"
              className="group mt-10 inline-flex w-full items-center justify-center gap-3 bg-harvest px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-forest transition-colors duration-300 hover:bg-harvestHover disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? t("sending") : t("sendEnquiry")}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
